import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Search, Briefcase, Users, UserCircle2, Building2, FileSpreadsheet, ShieldAlert, Clock, Camera, Loader2 } from 'lucide-react';

const BACKEND_URL = window.__BACKEND_URL__;
const API = `${BACKEND_URL}/api`;

const TABS = [
  { key: 'all', label: 'All', icon: Search },
  { key: 'jobs', label: 'Jobs', icon: Briefcase },
  { key: 'staff', label: 'Staff', icon: Users },
  { key: 'teams', label: 'Teams', icon: UserCircle2 },
  { key: 'customers', label: 'Customers', icon: Building2 },
  { key: 'time_entries', label: 'Time Entries', icon: Clock },
  { key: 'photos', label: 'Photos', icon: Camera },
  { key: 'reports', label: 'Reports', icon: FileSpreadsheet },
  { key: 'announcements', label: 'Announcements', icon: ShieldAlert },
];

const CATEGORY_LABEL = {
  jobs: 'Jobs',
  staff: 'Staff',
  teams: 'Teams',
  customers: 'Customers',
  time_entries: 'Time Entries',
  photos: 'Photos',
  reports: 'Reports',
  announcements: 'Safety Announcements',
};

function StatusBadge({ status }) {
  if (!status) return null;
  const s = (status || '').toLowerCase();
  return <span className="inline-block px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-[#1A2433] text-[#A0AEC0]">{s}</span>;
}

export default function SearchPage() {
  const { token, user, isAdmin, isSupervisor, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const initialQ = params.get('q') || '';
  const initialCat = params.get('category') || 'all';
  const [q, setQ] = useState(initialQ);
  const [category, setCategory] = useState(initialCat);
  const [sort, setSort] = useState('relevant');
  const [results, setResults] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const allowed = isAdmin || isSupervisor;

  const run = useCallback(async (query, cat, sortBy) => {
    if (!query || query.trim().length < 2) {
      setResults(null);
      setTotal(0);
      return;
    }
    try {
      setLoading(true);
      const res = await axios.get(`${API}/search`, {
        params: {
          q: query.trim(),
          category: cat === 'all' ? undefined : cat,
          sort: sortBy,
          limit_per_category: cat === 'all' ? 5 : 50,
        },
        headers: { Authorization: `Bearer ${token}` },
      });
      setResults(res.data.results || {});
      setTotal(res.data.total || 0);
    } catch (e) {
      console.error('Search failed', e);
      setResults({});
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) return;
    run(q, category, sort);
  }, [token, q, category, sort, run]);

  // Update URL whenever query/category change
  useEffect(() => {
    const usp = new URLSearchParams();
    if (q) usp.set('q', q);
    if (category && category !== 'all') usp.set('category', category);
    const next = `/search${usp.toString() ? `?${usp.toString()}` : ''}`;
    if (location.pathname + location.search !== next) {
      navigate(next, { replace: true });
    }
  }, [q, category, navigate, location.pathname, location.search]);

  if (authLoading) {
    return <div className="min-h-screen flex items-center justify-center" style={{ background: '#0B1321', color: '#A0AEC0' }}>Loading...</div>;
  }
  if (!allowed) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: '#0B1321' }} data-testid="search-forbidden">
        <p className="text-slate-400">Search is available to Admins and Supervisors only.</p>
      </div>
    );
  }

  const groupKeys = (category === 'all' ? ['jobs', 'staff', 'teams', 'customers', 'time_entries', 'photos', 'reports', 'announcements'] : [category]);

  return (
    <div className="min-h-screen" style={{ background: '#0B1321' }} data-testid="search-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Search input */}
        <div className="flex items-center gap-2 rounded-xl px-3 py-2" style={{ background: '#0F1923', border: '1px solid #2D3748' }}>
          <Search className="w-4 h-4 flex-shrink-0" style={{ color: '#64748B' }} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search staff, jobs, customers, teams..."
            className="flex-1 bg-transparent text-white text-base outline-none px-2 py-1 placeholder:text-slate-500"
            data-testid="search-page-input"
            autoFocus
          />
        </div>

        {/* Tabs + sort */}
        <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
          <div className="flex items-center gap-2 overflow-x-auto" data-testid="search-tabs">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = category === t.key;
              const count = t.key === 'all' ? total : (results?.[t.key]?.length || 0);
              return (
                <button
                  key={t.key}
                  onClick={() => setCategory(t.key)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap"
                  style={{
                    background: active ? '#1E88E5' : '#1A2433',
                    color: active ? '#FFFFFF' : '#A0AEC0',
                    border: '1px solid ' + (active ? '#1E88E5' : '#2D3748'),
                  }}
                  data-testid={`search-tab-${t.key}`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {t.label}
                  {q && count > 0 && <span className="ml-1 px-1.5 rounded text-[10px]" style={{ background: 'rgba(255,255,255,0.15)' }}>{count}</span>}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs" style={{ color: '#A0AEC0' }}>Sort by</label>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="text-xs rounded px-2 py-1 outline-none"
              style={{ background: '#1A2433', color: '#FFFFFF', border: '1px solid #2D3748' }}
              data-testid="search-sort-select"
            >
              <option value="relevant">Most Relevant</option>
              <option value="recent">Most Recent</option>
            </select>
          </div>
        </div>

        {/* Body */}
        <div className="mt-6 space-y-6">
          {loading && (
            <div className="flex items-center gap-2 p-4 text-sm text-slate-400">
              <Loader2 className="w-4 h-4 animate-spin" /> Searching…
            </div>
          )}
          {!loading && q.length < 2 && (
            <p className="text-sm" style={{ color: '#A0AEC0' }}>Type at least 2 characters to search.</p>
          )}
          {!loading && q.length >= 2 && total === 0 && (
            <div className="text-center py-10" data-testid="search-page-empty">
              <p className="text-sm" style={{ color: '#A0AEC0' }}>No results for <span className="text-white font-semibold">"{q}"</span></p>
              <p className="text-xs mt-1" style={{ color: '#64748B' }}>Try a different keyword or remove filters.</p>
            </div>
          )}
          {!loading && q.length >= 2 && total > 0 && groupKeys.map((k) => {
            const items = results?.[k] || [];
            if (items.length === 0) return null;
            return (
              <section key={k} data-testid={`search-section-${k}`}>
                <h3 className="text-xs uppercase font-bold tracking-wider mb-3" style={{ color: '#A0AEC0' }}>{CATEGORY_LABEL[k]} <span className="ml-2" style={{ color: '#64748B' }}>{items.length}</span></h3>
                <div className="space-y-2">
                  {items.map((item, idx) => (
                    <button
                      key={item.id || item.key || idx}
                      onClick={() => navigate(item.url)}
                      className="w-full text-left rounded-xl p-3 transition-all hover:bg-white/5"
                      style={{ background: '#0F1923', border: '1px solid #2D3748' }}
                      data-testid={`search-page-result-${k}-${idx}`}
                    >
                      {k === 'jobs' && (
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              {item.job_number && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded text-[#1E88E5] bg-[#1E88E5]/15">{item.job_number}</span>}
                              <span className="text-sm text-white font-semibold truncate">{item.title}</span>
                            </div>
                            <p className="text-xs mt-1" style={{ color: '#A0AEC0' }}>
                              {item.customer_name || '—'}{item.worker_name ? ` · ${item.worker_name}` : ''}{item.address ? ` · ${item.address}` : ''}
                            </p>
                          </div>
                          <StatusBadge status={item.status} />
                        </div>
                      )}
                      {k === 'staff' && (
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <p className="text-sm text-white font-semibold truncate">{item.name}</p>
                            <p className="text-xs mt-1" style={{ color: '#A0AEC0' }}>
                              {item.role}{item.team_name ? ` · ${item.team_name}` : ''}{item.email ? ` · ${item.email}` : ''}
                            </p>
                          </div>
                          {item.clocked_in && (
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded text-[#00C896] bg-[#00C896]/15">ON CLOCK</span>
                          )}
                        </div>
                      )}
                      {k === 'teams' && (
                        <div>
                          <p className="text-sm text-white font-semibold truncate">{item.name}</p>
                          <p className="text-xs mt-1" style={{ color: '#A0AEC0' }}>
                            {item.supervisor_name ? `Supervisor: ${item.supervisor_name}` : 'No supervisor'} · {item.member_count} member{item.member_count !== 1 ? 's' : ''}
                          </p>
                        </div>
                      )}
                      {k === 'customers' && (
                        <div>
                          <p className="text-sm text-white font-semibold truncate">{item.name}</p>
                          <p className="text-xs mt-1" style={{ color: '#A0AEC0' }}>
                            {item.job_count} job{item.job_count !== 1 ? 's' : ''}{item.address ? ` · ${item.address}` : ''}
                          </p>
                        </div>
                      )}
                      {k === 'reports' && (
                        <div>
                          <p className="text-sm text-white font-semibold truncate">{item.title}</p>
                          <p className="text-xs mt-1" style={{ color: '#A0AEC0' }}>Open report</p>
                        </div>
                      )}
                      {k === 'announcements' && (
                        <div>
                          <p className="text-sm text-white font-semibold truncate">{item.title}</p>
                          <p className="text-xs mt-1" style={{ color: '#A0AEC0' }}>
                            {item.priority ? item.priority.toUpperCase() : 'INFO'}{item.created_at ? ` · ${new Date(item.created_at).toLocaleDateString()}` : ''}
                          </p>
                        </div>
                      )}
                      {k === 'time_entries' && (() => {
                        const ci = item.clock_in ? new Date(item.clock_in) : null;
                        const co = item.clock_out ? new Date(item.clock_out) : null;
                        const dateStr = ci ? ci.toLocaleDateString() : '—';
                        const ciStr = ci ? ci.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—';
                        const coStr = co ? co.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'still on clock';
                        return (
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <p className="text-sm text-white font-semibold truncate">{item.user_name}</p>
                              <p className="text-xs mt-1" style={{ color: '#A0AEC0' }}>
                                {dateStr} · {ciStr} → {coStr}{item.duration_hours != null ? ` · ${item.duration_hours}h` : ''}
                              </p>
                            </div>
                            {item.active && (
                              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded text-[#00C896] bg-[#00C896]/15">ACTIVE</span>
                            )}
                          </div>
                        );
                      })()}
                      {k === 'photos' && (
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              {item.job_number && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded text-[#1E88E5] bg-[#1E88E5]/15">{item.job_number}</span>}
                              <span className="text-sm text-white font-semibold truncate">{item.job_title}</span>
                            </div>
                            <p className="text-xs mt-1" style={{ color: '#A0AEC0' }}>
                              {item.customer_name || '—'} · {item.photo_count} photo{item.photo_count !== 1 ? 's' : ''}
                            </p>
                          </div>
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded text-[#EC4899] bg-[#EC4899]/15">VIEW</span>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
}
